import React, { useEffect, useRef, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Paperclip, Upload, Trash2, Loader2, FileText, Clock, Presentation, ListChecks, FileQuestion, Download } from "lucide-react";

const KIND_META = {
  ppt:       { icon: Presentation, label: "PPT / Slides" },
  worksheet: { icon: ListChecks,   label: "Worksheet" },
  quiz:      { icon: FileQuestion, label: "Quiz" },
  other:     { icon: FileText,     label: "Other" },
};

async function downloadAdminAttachment(sessionId, att) {
  try {
    const r = await api.get(`/admin/jidnyasa/sessions/${sessionId}/attachments/${att.id}/download`,
                              { responseType: "blob" });
    const blob = new Blob([r.data], { type: r.headers?.["content-type"] || "application/octet-stream" });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = att.filename || "attachment";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
  } catch (e) {
    toast.error("Could not download the file");
  }
}

function humanSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function daysLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  const d = Math.ceil(ms / 86400000);
  return d;
}

export default function JidnyasaAttachments({ sessionId, backendUrl, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingKind, setUploadingKind] = useState(null);
  const fileRefs = { ppt: useRef(), worksheet: useRef(), quiz: useRef(), other: useRef() };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/jidnyasa/sessions/${sessionId}/attachments`);
      setItems(r.data.attachments || []);
      if (r.data.purged) toast.info(`Removed ${r.data.purged} expired file(s) from storage`);
      onChange && onChange(r.data.attachments || []);
    } catch (e) {
      toast.error("Could not load attachments");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (sessionId) load(); /* eslint-disable-next-line */ }, [sessionId]);

  const onPick = async (kind, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File must be under 25 MB");
      return;
    }
    setUploadingKind(kind);
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", f);
    try {
      await api.post(`/admin/jidnyasa/sessions/${sessionId}/attach`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${KIND_META[kind].label} uploaded · expires in 14 days`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploadingKind(null); }
  };

  const remove = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    try {
      await api.delete(`/admin/jidnyasa/sessions/${sessionId}/attachments/${att.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-black/5" data-testid="jidnyasa-attachments">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          <Paperclip className="w-3.5 h-3.5"/> Session kit · auto-deletes after 14 days
        </div>
      </div>

      {/* Upload buttons */}
      <div className="flex flex-wrap gap-2 mb-3">
        {Object.entries(KIND_META).map(([k, meta]) => {
          const Icon = meta.icon;
          return (
            <label key={k}
                    className="btn-ghost text-xs px-3 py-1.5 cursor-pointer inline-flex items-center gap-1.5"
                    data-testid={`jidnyasa-upload-${k}`}>
              {uploadingKind === k ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Upload className="w-3.5 h-3.5"/>}
              <Icon className="w-3.5 h-3.5"/>
              {meta.label}
              <input ref={fileRefs[k]} type="file"
                      className="hidden"
                      accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.gif,.webp"
                      onChange={(e) => onPick(k, e)}/>
            </label>
          );
        })}
      </div>

      {/* Attachment list */}
      {loading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">
          No files yet. Upload PPT / worksheet / quiz — subscribers can download until they expire.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((a) => {
            const meta = KIND_META[a.kind] || KIND_META.other;
            const Icon = meta.icon;
            const left = daysLeft(a.expires_at);
            return (
              <div key={a.id} className="flex items-center gap-3 text-sm px-3 py-2 rounded-xl bg-white border border-black/5"
                    data-testid={`jidnyasa-attachment-${a.id}`}>
                <Icon className="w-4 h-4 text-[hsl(var(--neu-orange))] shrink-0"/>
                <button onClick={() => downloadAdminAttachment(sessionId, a)}
                         className="font-medium truncate hover:underline flex-1 min-w-0 text-left inline-flex items-center gap-1.5">
                  {a.filename}
                  <Download className="w-3 h-3 text-muted-foreground shrink-0"/>
                </button>
                <span className="text-xs text-muted-foreground shrink-0">{humanSize(a.size)}</span>
                <span className="text-xs text-muted-foreground shrink-0 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3"/>
                  {left != null && left > 0 ? `${left}d left` : "expiring"}
                </span>
                <button onClick={() => remove(a)}
                         className="text-destructive hover:bg-destructive/10 rounded-full p-1"
                         data-testid={`jidnyasa-attachment-delete-${a.id}`}>
                  <Trash2 className="w-3.5 h-3.5"/>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
